import { ChartOptions } from 'chart.js';
import { IUserLoginData, IUserLoginStats } from './UserLineActivityChart';
import 'chartjs-adapter-date-fns';
import { t } from 'i18next';
import i18next from 'i18next';
import 'chartjs-adapter-date-fns';
import { ru, enUS, Locale } from 'date-fns/locale';
import { loginColorsArray } from '../../colorsArray/colorsArray';
import dayjs from 'dayjs';

export type MetaEntry = {
  '1_day': number;
  '7_day': number;
  '30_day': number;
};

export interface ChartDatasetWithMeta {
  label: string;
  data: number[];
  borderColor: string;
  tension?: number;
  meta?: MetaEntry[];
}

const localeMap: Record<string, Locale> = {
  ru: ru,
  en: enUS,
};

const currentLang = i18next.language;

export const getDatasets = (
  data: { date: string; loginData: IUserLoginData }[],
  filters: string[] | null
) => {
  const colors = loginColorsArray.map(item => item.backgroundColor);
  const datasets: ChartDatasetWithMeta[] = [];

  if (filters && filters.length > 0) {
    filters.forEach((filterName, index) => {
      const dataByFilter = data.map(({ loginData }) => {
        return loginData?.[filterName]?.login_count ?? 0;
      });

      const metaByFilter = data.map(({ loginData }) => {
        const entry = loginData?.[filterName] as IUserLoginStats;
        return {
          '1_day': entry?.['1_day'] ?? 0,
          '7_day': entry?.['7_day'] ?? 0,
          '30_day': entry?.['30_day'] ?? 0,
        };
      });

      datasets.push({
        label: filterName,
        data: dataByFilter,
        borderColor: colors[index % colors.length],
        meta: metaByFilter,
      });
    });
  } else {
    const values = data.map(({ loginData }) => {
      if (
        'login_count' in loginData &&
        typeof loginData.login_count === 'number'
      ) {
        return loginData.login_count;
      }
      return 0;
    });

    datasets.push({
      label: t('total'),
      data: values,
      borderColor: colors[0],
    });
  }

  return datasets;
};

export const getOptions = (
  tooltipRef: React.RefObject<HTMLDivElement>,
  metaData: MetaEntry[][]
): ChartOptions<'line'> => ({
  responsive: true,
  interaction: {
    mode: 'index',
    intersect: false,
    axis: 'x',
  },
  plugins: {
    legend: {
      display: true,
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 10,
        boxHeight: 10,
        color: '#4B465C',
        font: {
          size: 15,
        },
        generateLabels: chart => {
          return chart.data.datasets.map((dataset, i) => {
            const rawLabel = dataset?.label || '';
            let label = rawLabel;

            if (rawLabel.startsWith('>=')) {
              label = `${rawLabel.slice(2)} ${t('and_more')} ${t(
                'times'
              ).toLowerCase()}`;
            } else if (rawLabel.startsWith('<=')) {
              label = `${rawLabel.slice(2)} ${t('and_less')} ${t(
                'times'
              ).toLowerCase()}`;
            } else if (rawLabel.startsWith('>')) {
              label = `${t('more')} ${rawLabel.slice(1)} ${t(
                'times'
              ).toLowerCase()}`;
            } else if (rawLabel.startsWith('<')) {
              label = `${t('less')} ${rawLabel.slice(1)} ${t(
                'times'
              ).toLowerCase()}`;
            } else if (rawLabel.startsWith('=')) {
              label = `${rawLabel.slice(1)} ${t('times').toLowerCase()}`;
            }

            return {
              text: label,
              fillStyle: dataset.borderColor as string,
              strokeStyle: dataset.borderColor as string,
              lineWidth: 0,
              hidden: !chart.isDatasetVisible(i),
              index: i,
              pointStyle: 'circle',
            };
          });
        },
      },
    },
    tooltip: {
      enabled: false,
      external: function (context) {
        const tooltip = context.tooltip;
        const tooltipEl = tooltipRef.current;
        if (!tooltipEl) return;

        if (tooltip.opacity === 0) {
          tooltipEl.style.opacity = '0';
          return;
        }

        const { chart } = context;
        const { dataPoints } = tooltip;
        const date = tooltip.title?.[0] ?? '';
        dayjs.locale(i18next.language || 'en');

        let html = `<div class="custom-tooltip"><div class="tooltip-title">${dayjs(
          date,
          'DD.MM.YYYY'
        ).format('D MMMM')}</div>`;

        const hasMeta =
          !!metaData?.[dataPoints[0]?.datasetIndex]?.[dataPoints[0]?.dataIndex];

        if (hasMeta) {
          html += `<div class="tooltip-subtitle">${t(
            'activity_by_period'
          )}</div>
      <div class="table-containrt">
        <table class="tooltip-table">
          <thead>
            <tr>
              <th></th>
              <th>${t('1_day')}</th>
              <th>${t('7_day')}</th>
              <th>${t('30_day')}</th>
            </tr>
          </thead>
          <tbody>
    `;

          dataPoints.forEach(dp => {
            const dataset = chart.data.datasets[
              dp.datasetIndex
            ] as ChartDatasetWithMeta;
            const color = dataset.borderColor as string;
            const label = dataset?.label || '';

            let finalLabel = label;

            if (label.startsWith('>=')) {
              finalLabel = `${label.slice(2)} ${t('and_more')} ${t(
                'times'
              ).toLowerCase()}`;
            } else if (label.startsWith('<=')) {
              finalLabel = `${label.slice(2)} ${t('and_less')} ${t(
                'times'
              ).toLowerCase()}`;
            } else if (label.startsWith('>')) {
              finalLabel = `${t('more')} ${label.slice(1)} ${t(
                'times'
              ).toLowerCase()}`;
            } else if (label.startsWith('<')) {
              finalLabel = `${t('less')} ${label.slice(1)} ${t(
                'times'
              ).toLowerCase()}`;
            } else if (label.startsWith('=')) {
              finalLabel = `${label.slice(1)} ${t('times').toLowerCase()}`;
            }

            const meta = metaData?.[dp.datasetIndex]?.[dp.dataIndex];

            html += `
        <tr>
          <td><span class="dot" style="background:${color}"></span>${finalLabel}</td>
          <td class="tooltip-value"><span>${meta?.['1_day'] ?? '-'}</span></td>
          <td class="tooltip-value"><span>${meta?.['7_day'] ?? '-'}</span></td>
          <td class="tooltip-value"><span>${meta?.['30_day'] ?? '-'}</span></td>
        </tr>
      `;
          });

          html += `</tbody></table></div>`;
        } else {
          html += `<div class="tooltip-subtitle">${t(
            'total'
          )}</div><ul class="simple-tooltip-list">`;

          dataPoints.forEach(dp => {
            const dataset = chart.data.datasets[dp.datasetIndex];
            const color = dataset.borderColor as string;
            const label = dataset.label;
            const value = dataset.data?.[dp.dataIndex] ?? '-';

            html += `
        <li>
          <span class="dot" style="background:${color}"></span>
          <span class="tooltip-label">${label}</span>:
          <span class="tooltip-value">${value}</span>
        </li>
      `;
          });

          html += `</ul>`;
        }

        html += `</div>`;

        tooltipEl.innerHTML = html;

        const { offsetLeft, offsetTop } = chart.canvas;
        const chartRect = chart.canvas.getBoundingClientRect();
        const tooltipWidth = tooltipEl.offsetWidth;
        const tooltipHeight = tooltipEl.offsetHeight;

        let left = offsetLeft + tooltip.caretX;
        let top = offsetTop + tooltip.caretY;

        if (left + tooltipWidth > chartRect.width) {
          left = chartRect.width - tooltipWidth - 10;
        }
        if (top + tooltipHeight > chartRect.height) {
          top = chartRect.height - tooltipHeight - 10;
        }
        if (left < 0) left = 10;

        tooltipEl.style.opacity = '1';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.left = `${left}px`;
        tooltipEl.style.top = `-200px`;
        tooltipEl.style.pointerEvents = 'none';
      },
    },
  },
  scales: {
    x: {
      type: 'time',
      adapters: {
        date: {
          locale: localeMap[currentLang] || enUS,
        },
      },
      time: {
        unit: 'day',
        tooltipFormat: 'dd.MM.yyyy',
        displayFormats: {
          day: 'dd.MM',
        },
      },
      ticks: {
        color: '#6B7280',
      },
      grid: {
        display: false,
      },
    },
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        color: '#6B7280',
      },
      grid: {
        color: '#E5E7EB',
      },
    },
  },
  elements: {
    point: {
      radius: 0,
      hoverRadius: 0,
    },
  },
  maintainAspectRatio: false,
});
